import type { DraftEvent, NostrEvent } from "../types"

/** How the current session signs events. */
export type SignerMethod = "nip07" | "nsec" | "npub"

export interface Signer {
  readonly method: SignerMethod
  getPublicKey(): Promise<string>
  signEvent(draft: DraftEvent): Promise<NostrEvent>
  nip04Encrypt?(pubkey: string, plaintext: string): Promise<string>
  nip04Decrypt?(pubkey: string, ciphertext: string): Promise<string>
}

export class SignerError extends Error {
  readonly code: "unavailable" | "rejected" | "read-only" | "invalid"

  constructor(message: string, code: SignerError["code"] = "unavailable") {
    super(message)
    this.name = "SignerError"
    this.code = code
  }
}

export interface Nip07Provider {
  getPublicKey: () => Promise<string>
  signEvent: (event: DraftEvent) => Promise<NostrEvent>
  nip04?: {
    encrypt?: (pubkey: string, plaintext: string) => Promise<string>
    decrypt?: (pubkey: string, ciphertext: string) => Promise<string>
  }
}

/** NIP-07 browser extension bridge (Alby, nos2x, ...). */
export class Nip07Signer implements Signer {
  readonly method = "nip07" as const
  private readonly provider: Nip07Provider

  constructor(provider: Nip07Provider) {
    this.provider = provider
  }

  async getPublicKey(): Promise<string> {
    const pubkey = await this.provider.getPublicKey()
    if (typeof pubkey !== "string" || !/^[0-9a-f]{64}$/.test(pubkey)) {
      throw new SignerError("the extension returned an invalid public key", "invalid")
    }
    return pubkey
  }

  async signEvent(draft: DraftEvent): Promise<NostrEvent> {
    try {
      const event = await this.provider.signEvent(draft)
      if (!event?.id || !event.sig) throw new SignerError("the extension did not sign the event", "invalid")
      return event
    } catch (error) {
      if (error instanceof SignerError) throw error
      throw new SignerError(
        error instanceof Error ? error.message : "signing was rejected",
        "rejected",
      )
    }
  }

  async nip04Encrypt(pubkey: string, plaintext: string): Promise<string> {
    if (!this.provider.nip04?.encrypt) {
      throw new SignerError("the extension does not support NIP-04 encryption")
    }
    return this.provider.nip04.encrypt(pubkey, plaintext)
  }

  async nip04Decrypt(pubkey: string, ciphertext: string): Promise<string> {
    if (!this.provider.nip04?.decrypt) {
      throw new SignerError("the extension does not support NIP-04 decryption")
    }
    return this.provider.nip04.decrypt(pubkey, ciphertext)
  }
}

/** Detects an installed NIP-07 extension. */
export function getNip07Provider(): Nip07Provider | undefined {
  if (typeof window === "undefined") return undefined
  const candidate = (window as unknown as { nostr?: Nip07Provider }).nostr
  if (!candidate || typeof candidate.getPublicKey !== "function") return undefined
  if (typeof candidate.signEvent !== "function") return undefined
  return candidate
}
